import { useEffect, useRef, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { Card, Col, Dropdown, Form } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Sentiment } from 'assets/js/classes/BootstrapStyles'
import Dashboard from 'assets/js/classes/Dashboard'
import DragDrop from 'assets/js/classes/DragDrop'
import Toast from 'assets/js/classes/Toast'
import FormButton from 'components/forms/FormButton'
import FormControl from 'components/forms/FormControl'
import { StrictModeDroppable } from 'components/ui/StrictModeDroppable'
import DynamicBoardTask from '../components/DynamicBoard/Task'
import { FormControlProvider } from 'contexts/FormControlContext'
import { stateUpdateDashboard, useDashboardDispatch, useDashboardState } from 'contexts/DashboardContext'
import { stateUpdateSearchBar, useSearchBarDispatch, useSearchBarState } from 'contexts/SearchBarContext'
import { stateUpdateSidebar, useSidebarDispatch, useSidebarState } from 'contexts/SidebarContext'
import { updateToasts, useToastsDispatch, useToastsState } from 'contexts/ToastsContext'
import Skeleton from 'react-loading-skeleton'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import handleApiCall from 'assets/js/utilities/handleApiCall'
import ApiRoutes from 'assets/js/classes/ApiRoutes'
import { AxiosMethods } from 'assets/js/utilities/axios'
import TaskDropZone from './TaskDropZone'
import removeDuplicateObjects from 'assets/js/utilities/removeDuplicateObjects'

class InfiniteScroll {
  static #getElementIndex(element, array) {
    return array.length > 0 ? array.findIndex(value => element.id === value.id) : -1;
  }
  
  static appendTasks(tasks, bucket, buckets) {
    const index = InfiniteScroll.#getElementIndex(bucket, buckets);

    if (index === -1) return buckets;

    if (! buckets.at(index).tasks?.data) buckets.at(index).tasks = { data: [] };

    // CHECK THAT TASKS BELONG TO BUCKETS DUE TO RENDERING BUG WHEN API CALL IS MADE RIGHT BEFORE HIDING TRASHED BUCKETS
    if (! tasks.data.every(task => task.bucket_id === buckets.at(index).id)) return buckets;
    
    // APPEND TASKS
    buckets.at(index).tasks.data = [...buckets.at(index).tasks.data, ...tasks.data];
  
    // REMOVE DUPLICATES
    buckets.at(index).tasks.data = removeDuplicateObjects(buckets.at(index).tasks.data, 'id', 'updated_at');

    // ENSURE ORDER IS SET
    buckets.at(index).tasks.data.sort((a, b) => a.order_index > b.order_index ? 1 : -1);

    return buckets;
  }
}

export default function AllProjectsColumn({ bucket, index }) {
  // CONTEXT PARAMS - DASHBOARD
  const { buckets, bucketsRef, pageReset } = useDashboardState();
  const dashboardDispatch = useDashboardDispatch();
  
  // CONTEXT - SEARCH BAR
  const { dataset, datasetRef, filtered } = useSearchBarState();
  const searchBarDispatch = useSearchBarDispatch();

  // CONTEXT - SIDEBAR
  const { buckets: sidebarBuckets, bucketsRef: sidebarBucketsRef } = useSidebarState();
  const sidebarDispatch = useSidebarDispatch();

  // CONTEXT - TOASTS
  const { toasts } = useToastsState();
  const toastsDispatch = useToastsDispatch();

  // STATE PARAMS
  const [editTitle, setEditTitle] = useState(false);
  const [showTaskSkeleton, setShowTaskSkeleton] = useState(true);
  const localPageReset = useRef(null);
  localPageReset.current = pageReset.current?.[bucket.id];
  const loading = useRef(false);
  const page = useRef(bucket.tasks?.current_page ?? 0);
  useEffect(() => {
    if (pageReset.current?.[bucket.id]) {
      page.current = 0;
      setShowTaskSkeleton(true); 
      pageReset.current = { ...pageReset.current, [bucket.id]: false };
    }
  }, [localPageReset.current]);

  useEffect(() => {
    pageReset.current = { ...pageReset.current, [bucket.id]: pageReset.current?.[bucket.id] ?? undefined };
  }, []);

  const skeletonRef = useIntersectionObserver(async (entry) => {
    if (filtered || loading.current || !entry.isIntersecting) return;
    
    loading.current = true;
    page.current += 1;

    try {
      await handleApiCall({
        apiMethod: AxiosMethods.GET,
        apiRoute: ApiRoutes.BUCKETS_TASKS_PAGINATION(bucket.id, page.current),
        callback: ({ tasks }) => {
          if (tasks.data.length === 0) {
            setShowTaskSkeleton(false); 
            return;
          }
          stateUpdateDashboard(dashboardDispatch, { buckets: InfiniteScroll.appendTasks(tasks, bucket, bucketsRef.current) });
          stateUpdateSearchBar(searchBarDispatch, { dataset: InfiniteScroll.appendTasks({ data: [] }, bucket, bucketsRef.current) });
        },
        toasts,
        toastsDispatch,
      });
    } catch (error) {
      console.error(error)
      setShowTaskSkeleton(false);
    } finally {
      loading.current = false;
    }
  });

  const SkeletonWrapper = ({ children }) => <div hidden={filtered} ref={skeletonRef}>{children}</div>;

  const handleOnClick = () => {  // TODO: remove this?
    if (!filtered) {
      setEditTitle(true);
    } else {
      // THROW TOAST TO USER
      toasts.unshift(new Toast(
        'Error', 
        'Please clear your search filter before trying to rename the list.',
        { autohide: true, sentiment: Sentiment.DANGER },
      ));
      updateToasts(toastsDispatch, { toasts });
    }
  }

  return (
    <Draggable 
      draggableId={`draggable-bucket-${bucket.id}`} 
      index={index}
    >
      {(provided, snapshot) => (
        <Col 
          xs={12} md={6} lg={5} xl={4}
          {...provided.draggableProps}
          ref={provided.innerRef}
        >
          <Card className={`bucketCard border-0 pt-2 my-2 align-self-start ${snapshot.isDragging ? 'shadow' : 'shadow-sm'}`}>
            <span className="d-flex align-items-stretch">

              <div 
                className="p-2 transparentBorder" 
                {...provided.dragHandleProps}
              >
                <FontAwesomeIcon icon={['fas', 'grip-dots-vertical']} fixedWidth />
              </div>
              
              <>
                { editTitle && 
                  <Form 
                    autoComplete="false"
                    className="pr-2 w-100"
                    onSubmit={e => e.preventDefault()}
                  >
                    <FormControlProvider>
                      <FormControl
                        autoFocus={editTitle}
                        className="noBorder border-0 shadow-sm"
                        modelContext={{ 
                          bucket,
                          dashboard: {
                            dataset: buckets, 
                            dispatch: dashboardDispatch,
                            prop: 'buckets',
                            update: stateUpdateDashboard,
                          },
                          searchBar: {
                            dataset: dataset,
                            dispatch: searchBarDispatch,
                            prop: 'dataset',
                            update: stateUpdateSearchBar,
                          },
                          sidebar: {
                            dataset: sidebarBuckets,
                            dispatch: sidebarDispatch,
                            prop: 'buckets',
                            update: stateUpdateSidebar,
                          },
                        }}
                        name="title"
                        onBlur={() => setEditTitle(false)}  // TODO: change this to submit/save?
                        onKeyDown={Dashboard.handleKeyDown}
                        placeholder="Title..."
                        required
                        value={bucket.title}
                      />
                    </FormControlProvider>
                  </Form>
                }

                { !editTitle && 
                  <div 
                    className="bucketTitle p-2 pl-0 flex-fill" 
                    onClick={handleOnClick}
                  >
                    {bucket.title}
                  </div>
                }

                { !editTitle && 
                  <Dropdown 
                    align="end" 
                    className="d-flex align-items-center"
                  >
                    <Dropdown.Toggle 
                      className="rounded-0 align-self-stretch"
                      variant="link" 
                    >
                      <FontAwesomeIcon icon={['fas', 'ellipsis']} color="#495057" fixedWidth />
                    </Dropdown.Toggle>
                    
                    <Dropdown.Menu>
                      <Dropdown.Item 
                        as={NavLink} 
                        end 
                        to={`/projects/new-task/${bucket.slug}`}
                      >
                        <FontAwesomeIcon icon={['fas', 'rectangle-history-circle-plus']} fixedWidth className="mr-1" />
                        New Task
                      </Dropdown.Item>

                      <Form 
                        autoComplete="false"
                        onSubmit={e => e.preventDefault()}
                      >
                        <FormControlProvider>
                          <FormButton
                            alert={{
                              title: 'Delete this List/Dashboard?',
                              message: bucket?.title,
                              options: {
                                confirmBtnBsStyle: 'danger',
                                type: 'danger',
                              }
                            }}
                            className="dropdown-item rounded-0 text-danger"
                            loadStatus="Deleting..."
                            modelContext={{ 
                              bucket,
                              dashboard: {
                                dataset: buckets, 
                                dispatch: dashboardDispatch,
                                prop: 'buckets',
                                update: stateUpdateDashboard,
                              },
                              searchBar: {
                                dataset,
                                dispatch: searchBarDispatch,
                                prop: 'dataset',
                                update: stateUpdateSearchBar,
                              },
                              sidebar: {
                                dataset: sidebarBuckets,
                                dispatch: sidebarDispatch,
                                prop: 'buckets',
                                update: stateUpdateSidebar,
                              },
                            }}
                            onClick={Dashboard.handleDeleteBucket}
                            variant="link"
                          >
                            <FontAwesomeIcon fixedWidth icon={['fas', 'trash']} className="mr-1" />
                            Delete List/Dashboard
                          </FormButton>
                        </FormControlProvider>
                      </Form>
                    </Dropdown.Menu>
                  </Dropdown>
                }
              </>
            </span>

            <StrictModeDroppable 
              droppableId={`droppable-bucket-${bucket.id}`} 
              type={DragDrop.TASKS}
            >
              {(provided, snapshot) => (
                <TaskDropZone 
                  className="bucketDropZone py-2 pl-2 pr-1" 
                  style={snapshot.isDraggingOver ? { background: 'rgba(247, 185, 36, 0.1)' } : {}}
                  provided={provided}
                >

                  {
                    // bucket.tasks?.data?.sort((a, b) => a.order_index > b.order_index ? 1 : -1)
                    bucket.tasks?.data?.map((task, index) => 
                      <DynamicBoardTask key={task.id} task={task} index={index} />
                    )
                  }
      
                  {provided.placeholder}
                  
                  { showTaskSkeleton 
                    &&
                    <Skeleton
                      className="mb-2 shadow-sm"
                      height={60}
                      wrapper={SkeletonWrapper}
                    />
                  }
                </TaskDropZone>
              )}
            </StrictModeDroppable>
          </Card>
        </Col>
      )}
    </Draggable>
  )
}